import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Icon from '../components/AppIcon';

const BlogListing = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isIndividualBlog, setIsIndividualBlog] = useState(false);
  const [allBlogs, setAllBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic blog loading using Vite's import.meta.glob
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setIsLoading(true);
        
        // Use Vite's import.meta.glob to dynamically import all blog files
        const blogModules = import.meta.glob('../blogs/*.jsx');
        const blogs = [];
        
        // Load all blog files dynamically
        for (const path in blogModules) {
          try {
            const module = await blogModules[path]();
            // Get the exported blog object (assuming each file exports one blog object)
            const blogData = Object.values(module)[0];
            if (blogData && blogData.slug && blogData.title) {
              blogs.push(blogData);
            } else {
              console.warn(`Invalid blog data in ${path}:`, blogData);
            }
          } catch (error) {
            console.error(`Error loading blog from ${path}:`, error);
          }
        }
        
        // Sort blogs by publish date (newest first)
        blogs.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
        
        setAllBlogs(blogs);
      } catch (error) {
        console.error('Error loading blogs:', error);
        setAllBlogs([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadBlogs();
  }, []);

  // Handle individual blog page
  useEffect(() => {
    if (slug && allBlogs.length > 0) {
      const blog = allBlogs.find(b => b.slug === slug);
      if (blog) {
        setSelectedBlog(blog);
        setIsIndividualBlog(true);
      } else {
        navigate('/blog');
      }
    } else if (!slug) {
      setIsIndividualBlog(false);
      setSelectedBlog(null);
    }
  }, [slug, navigate, allBlogs]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO 
          title="Blog"
          description="Email Validation Blog"
        />
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-105px)]">
          <div className="text-center">
            <div className="text-lg text-gray-600 mb-5">Loading blogs...</div>
            <div className="w-10 h-10 border-4 border-gray-200 border-t-primary rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions
  const getAllCategories = () => {
    const categories = [...new Set(allBlogs.map(blog => blog.category))];
    return categories;
  };

  const getAllTags = () => {
    const tags = [...new Set(allBlogs.flatMap(blog => blog.tags))];
    return tags;
  };

  const categories = ['All', ...getAllCategories()];
  const tags = ['All', ...getAllTags()];
  const filteredBlogs = allBlogs.filter(blog => {
    const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
    const matchesTag = selectedTag === 'All' || blog.tags.includes(selectedTag);
    const matchesSearch = searchTerm === '' || 
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesCategory && matchesTag && matchesSearch;
  });

  // Enhanced SEO Data
  const seoData = {
    title: 'Blog',
    description: 'Read our latest insights on email validation best practices, API integration guides, and deliverability optimization. Expert guides from OctalChip.',
    keywords: 'email validation blog, email verification blog, email marketing blog, deliverability blog, email API blog',
    canonical: 'https://mailvalidator.octalchip.com/blog',
    tags: ['email validation blog', 'email verification', 'email marketing', 'deliverability', 'email API'],
    breadcrumbs: [
      { name: 'Home', url: 'https://mailvalidator.octalchip.com/' },
      { name: 'Blog', url: 'https://mailvalidator.octalchip.com/blog' }
    ],
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "EmailValidator Development Blog",
      "description": "Expert insights on email validation, API integration, and deliverability optimization",
      "url": "https://mailvalidator.octalchip.com/blog",
      "publisher": {
        "@type": "Organization",
        "name": "OctalChip",
        "logo": {
          "@type": "ImageObject",
          "url": "https://mailvalidator.octalchip.com/favicon.svg"
        }
      },
        "blogPost": allBlogs.map(blog => ({
        "@type": "BlogPosting",
        "headline": blog.title,
        "description": blog.shortDescription,
        "url": `https://mailvalidator.octalchip.com/blog/${blog.slug}`,
        "datePublished": blog.publishDate,
        "dateModified": blog.publishDate
      }))
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // If we're on an individual blog page, render the blog detail view
  if (isIndividualBlog && selectedBlog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO 
          title={selectedBlog.title}
          description={selectedBlog.metaDescription}
          keywords={selectedBlog.keywords}
          canonical={`https://mailvalidator.octalchip.com/blog/${selectedBlog.slug}`}
          ogType="article"
          author="OctalChip"
          publishDate={selectedBlog.publishDate}
          modifiedDate={selectedBlog.publishDate}
          articleSection={selectedBlog.category}
          tags={selectedBlog.tags}
          breadcrumbs={[
            { name: 'Home', url: 'https://mailvalidator.octalchip.com/' },
            { name: 'Blog', url: 'https://mailvalidator.octalchip.com/blog' },
            { name: selectedBlog.title, url: `https://mailvalidator.octalchip.com/blog/${selectedBlog.slug}` }
          ]}
          structuredData={selectedBlog.structuredData}
        />
        
        <Header />
        
        {/* Individual Blog Page Content */}
        <div className="max-w-4xl mx-auto px-6 py-10">
          {/* Back Button */}
          <div className="mb-8">
            <a
              href="/blog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-gray-700 font-medium bg-white shadow-sm hover:bg-gray-50 transition-all duration-300 border border-gray-200"
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.transform = 'translateX(-4px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) icon.style.transform = 'translateX(0)';
              }}
            >
              <Icon name="ArrowLeft" size={16} style={{ transition: 'transform 0.3s ease' }} />
              Back to Blog
            </a>
          </div>

          {/* Blog Header */}
          <div className="mb-10">
            <div className="flex gap-3 mb-5 flex-wrap">
              <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                {selectedBlog.category}
              </span>
              <span className="bg-gray-200 text-gray-700 px-4 py-1 rounded-full text-sm font-medium">
                {selectedBlog.readTime}
              </span>
              <span className="bg-gray-200 text-gray-700 px-4 py-1 rounded-full text-sm font-medium">
                {formatDate(selectedBlog.publishDate)}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              {selectedBlog.title}
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {selectedBlog.shortDescription}
            </p>

            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-gray-600">
                <Icon name="Calendar" size={16} />
                <span className="text-sm">{formatDate(selectedBlog.publishDate)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Icon name="Clock" size={16} />
                <span className="text-sm">{selectedBlog.readTime}</span>
              </div>
            </div>
          </div>

          {/* Blog Content */}
          <div className="bg-white rounded-2xl p-8 md:p-12 mb-10 shadow-sm">
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
              {selectedBlog.content}
            </div>
          </div>

          {/* Internal and External Links */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
              {/* Internal Links */}
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1E293B', marginBottom: '20px' }}>
                  Related Resources
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedBlog.internalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      style={{
                        display: 'block',
                        padding: '12px 16px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        color: '#2d3748',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        border: '1px solid #e2e8f0'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e2e8f0';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f8fafc';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {link.text}
                    </a>
                  ))}
                </div>
              </div>

              {/* External Links */}
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1E293B', marginBottom: '20px' }}>
                  External Resources
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedBlog.externalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        padding: '12px 16px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '8px',
                        color: '#0369a1',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        border: '1px solid #bae6fd'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#e0f2fe';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = '#f0f9ff';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {link.text} ↗
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Default blog listing page
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        structuredData={seoData.structuredData}
        breadcrumbs={seoData.breadcrumbs}
      />
      
      <Header />

      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Email Validation Blog
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Stay updated with the latest insights, trends and best practices on email validation, 
              deliverability optimization, and API integration from our expert team.
            </p>
            <div className="flex justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2 text-primary">
                <Icon name="BookOpen" size={20} />
                <span className="font-semibold">{allBlogs.length} Articles</span>
              </div>
              <div className="flex items-center gap-2 text-primary">
                <Icon name="Users" size={20} />
                <span className="font-semibold">Expert Authors</span>
              </div>
              <div className="flex items-center gap-2 text-primary">
                <Icon name="TrendingUp" size={20} />
                <span className="font-semibold">Latest Insights</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white py-10 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 pl-14 py-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none text-base"
                />
                <Icon 
                  name="Search" 
                  size={20} 
                  className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name="X" size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Category and Tag Filters */}
            <div className="flex gap-6 justify-center flex-wrap">
              {/* Category Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none min-w-[150px]"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700">Tag</label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none min-w-[150px]"
                >
                  {tags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Cards Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-20">
              <div className="flex flex-col items-center gap-6">
                <Icon name="Search" size={48} className="text-gray-300" />
                <div>
                  <h3 className="text-2xl font-semibold text-gray-700 mb-3">
                    No articles found
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Try adjusting your search criteria or browse all articles
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedTag('All');
                }}
                className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {filteredBlogs.length} Article{filteredBlogs.length !== 1 ? 's' : ''} Found
                </h2>
                <p className="text-gray-600">
                  {selectedCategory !== 'All' && `Category: ${selectedCategory}`}
                  {selectedCategory !== 'All' && selectedTag !== 'All' && ' • '}
                  {selectedTag !== 'All' && `Tag: ${selectedTag}`}
                  {searchTerm && ` • Search: "${searchTerm}"`}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer h-full flex flex-col no-underline text-inherit"
                  >
                    {/* Blog Header */}
                    <div className="p-6 pb-4">
                      <div className="flex gap-3 mb-4 flex-wrap">
                        <span className="bg-primary text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {blog.category}
                        </span>
                        <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                          {blog.readTime}
                        </span>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-snug">
                        {blog.title}
                      </h3>

                      <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed mb-4">
                        {blog.shortDescription}
                      </p>
                    </div>

                    {/* Blog Footer */}
                    <div className="p-6 pt-0 mt-auto border-t border-gray-100">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Icon name="Calendar" size={14} />
                          <span className="text-xs">{formatDate(blog.publishDate)}</span>
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="mb-4 flex gap-2 flex-wrap">
                        {blog.tags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                        {blog.tags.length > 3 && (
                          <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs font-medium">
                            +{blog.tags.length - 3} more
                          </span>
                        )}
                      </div>

                      {/* Read More Button */}
                      <div className="text-center">
                        <span className="inline-flex items-center text-primary font-semibold text-sm cursor-pointer hover:text-primary-dark transition-colors">
                          Read More
                          <Icon name="ArrowRight" size={14} className="ml-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogListing;

